'use client'

import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ShoppingCart,
  Menu,
  X,
  Search,
  ChevronDown,
  User,
  LogOut,
  Home,
  Zap,
  Settings,
  Package,
  Heart,
  Bell,
  MapPin,
  Clock,
} from 'lucide-react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useCart } from '../context/CartContext'
import { useAuthModal } from '../context/AuthModalContext'
import { useWishlist } from '../context/WishlistContext'
import { motion, AnimatePresence } from 'framer-motion'
import { LaunchBadge } from './LaunchCountdown'
import { getImageUrl } from '../utils/imageUtils'

 const API = `${process.env.REACT_APP_BACKEND_URL}/api`

const Navbar = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuth()
  const { cartCount } = useCart()
  const { openAuth } = useAuthModal()
  const { wishlistCount } = useWishlist()

  // State management
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showResults, setShowResults] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [isNavbarVisible, setIsNavbarVisible] = useState(true)
  const profileRef = useRef(null)

  const searchRef = useRef(null)
  const mobileSearchRef = useRef(null)
  const lastScrollYRef = useRef(0)
useEffect(() => {
  function handleClickOutside(event) {
    if (
      mobileSearchRef.current &&
      !mobileSearchRef.current.contains(event.target)
    ) {
      setSearchExpanded(false)
      setShowResults(false)
    }
  }

  if (searchExpanded) {
    document.addEventListener('mousedown', handleClickOutside)
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside)
  }
}, [searchExpanded])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY < 20 || isMenuOpen || searchExpanded) {
        setIsNavbarVisible(true)
      } else if (currentScrollY > lastScrollYRef.current && currentScrollY > 120) {
        setIsNavbarVisible(false)
      } else if (currentScrollY < lastScrollYRef.current) {
        setIsNavbarVisible(true)
      }

      lastScrollYRef.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMenuOpen, searchExpanded])

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true)
        
        // Check if search query matches category/subcategory and filter accordingly
        const query = searchQuery.toLowerCase()
        let searchParams = { search: searchQuery, limit: 8 }
        
        // If searching for specific categories, use product_type filter
        if (query === 'gear') {
          searchParams.product_type = 'gear'
          delete searchParams.search
        } else if (query === 'jacket') {
          searchParams.spare_category_slug = 'jacket'
          delete searchParams.search
        } else if (query === 'lights') {
          searchParams.spare_category_slug = 'auxilary-lights'
          delete searchParams.search
        } else if (query === 'windshield') {
          searchParams.spare_category_slug = 'windshield'
          delete searchParams.search
        } else if (query === 'tank cover') {
          searchParams.spare_category_slug = 'tank-cover'
          delete searchParams.search
        } else if (query.includes('himalayan 411') && query.includes('tank cover')) {
          searchParams.spare_category_slug = 'tank-cover'
          searchParams.search = 'himalayan 411'
        } else if (query.includes('himalayan 450') && query.includes('tank cover')) {
          searchParams.spare_category_slug = 'tank-cover'
          searchParams.search = 'himalayan 450'
        }
        
        const res = await axios.get(`${API}/products`, { params: searchParams })
        setSearchResults(res.data || [])
        setShowResults(true)
      } catch (e) {
        console.error('[v0] Search error:', e)
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])
  useEffect(() => {
    const handler = (e) => {
      // Close profile dropdown
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false)
      }

      // Close search dropdown
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handler)

    return () => {
      document.removeEventListener('mousedown', handler)
    }
  }, [])



  const handleProductSelect = (id) => {
    setSearchQuery('')
    setShowResults(false)
    setSearchExpanded(false)
    navigate(`/products/${id}`)
  }

  const handleLogout = () => {
    logout()
    setIsMenuOpen(false)
    setShowProfileMenu(false)
  }

  const getProductImage = (product) => {
    let imagePath;
    
    // Handle combo products
    if (product.product_type === 'combo' || product.item_type === 'combo') {
      imagePath = product.thumbnail || product.images?.[0] || product.image || '/placeholder.png'
    }
    // Handle products with color images
    else if (product.colors?.length > 0 && product.color_images) {
      const firstColor = product.colors[0];
      
      // Try exact match first
      if (product.color_images[firstColor]?.[0]) {
        imagePath = product.color_images[firstColor][0];
      }
      // Try case-insensitive match
      else {
        const normalizedSearch = firstColor.trim().toLowerCase();
        const matchingKey = Object.keys(product.color_images).find(
          key => key.trim().toLowerCase() === normalizedSearch
        );
        
        if (matchingKey && product.color_images[matchingKey]?.[0]) {
          imagePath = product.color_images[matchingKey][0];
        }
        // If no match for first color, try any available color
        else {
          const firstAvailableColorImages = Object.values(product.color_images).find(images => images?.length > 0);
          imagePath = firstAvailableColorImages?.[0];
        }
      }
    }
    // Regular image handling
    else if (Array.isArray(product.images) && product.images.length > 0) {
      imagePath = product.images[0]
    } else if (typeof product.images === 'string') {
      imagePath = product.images
    } else {
      imagePath = product.thumbnail || product.image || '/placeholder.png'
    }
    
    return getImageUrl(imagePath);
  }

  return (
    <>
      {/* NAVBAR */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: isNavbarVisible ? 0 : -130 }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 w-full z-50 bg-black"
      >
        <div className="hidden lg:block bg-gradient-to-br from-[#102318] via-[#183326] to-[#0b1710] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-7 flex items-center justify-center gap-10">
            <button
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              HOME
            </button>
            <button
              onClick={() => navigate('/tank-cover')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              OUTRAN PRODUCTS
            </button>
            <button
              onClick={() => navigate('/products?product_type=gear')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              GEARS
            </button>
            <button
              onClick={() => navigate('/products?product_type=spare')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              BIKE PARTS
            </button>
            <button
              onClick={() => navigate('/products?product_type=accessory&spare_category_slug=auxilary-lights')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              AUXILIARY LIGHTS
            </button>
            <button
              onClick={() => navigate('/about')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              ABOUT US
            </button>
            <button
              onClick={() => navigate('/about-outrans')}
              className="text-white/70 hover:text-[#ffcc32] text-sm font-black uppercase tracking-wide z-50 relative"
            >
              ABOUT OUTRANS
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Navbar */}
          <div className="flex items-center justify-between h-16 gap-2 sm:gap-4 lg:h-20">
            {/* LEFT - LOGO & MOBILE MENU */}
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile Menu Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden text-white hover:text-[#ffcc32] transition p-1"
              >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </motion.button>

              {/* Logo */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate('/')}
                className="flex items-center gap-2 hover:opacity-80 transition flex-shrink-0 z-50 relative"
              >
                <img src="/Logo-removebg-preview.png" alt="RRIDE" className="w-10 h-10 lg:w-12 lg:h-12 object-contain" />
                <span className="text-lg lg:text-2xl font-black text-white hidden xs:inline">RRIDE</span>
                <span className="text-lg lg:text-2xl font-black text-[#ffcc32]">RRIDE</span>
              </motion.button>
            </div>

            {/* CENTER - DESKTOP SEARCH */}
            <div className="hidden lg:flex items-center gap-5 flex-1">
              <div className="flex-1 max-w-4xl" ref={searchRef}>
                <div className="relative">
                  <motion.div
                    whileFocus={{ scale: 1.02 }}
                    className="relative"
                  >
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-black/50" size={24} />
                    <input
                      value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setShowResults(true)}
                      placeholder="Search the store"
                      className="w-full h-16 pl-14 pr-5 bg-white border border-white text-black text-lg rounded-lg focus:outline-none focus:border-[#ffcc32] transition placeholder-black/50"
                    />
                  </motion.div>

                  <AnimatePresence>
                    {showResults && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full mt-2 w-full bg-black/95 border border-slate-800 rounded-lg shadow-2xl z-50 max-h-96 overflow-y-auto backdrop-blur-xl"
                      >
                        {/* Category Quick Filters */}
                        {searchQuery.length < 2 && (
                          <div className="p-4 border-b border-slate-800/30">
                            <p className="text-white/60 text-sm font-semibold mb-3 uppercase tracking-wide">Quick Categories</p>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { label: 'Tank Cover 411', filter: 'himalayan 411 tank cover' },
                                { label: 'Tank Cover 450', filter: 'himalayan 450 tank cover' },
                                { label: 'Lights', filter: 'lights' },
                                { label: 'Windshield', filter: 'windshield' },
                                { label: 'Gears', filter: 'gear' },
                                { label: 'Jackets', filter: 'jacket' },
                              ].map((cat, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setSearchQuery(cat.filter)}
                                  className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-full text-sm text-white hover:bg-[#ffcc32] hover:text-black hover:border-[#ffcc32] transition"
                                >
                                  {cat.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {loading && searchQuery.length >= 2 && (
                          <div className="p-4 text-white/60 text-base flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#ffcc32] border-t-transparent"></div>
                            <span className="ml-3">Searching...</span>
                          </div>
                        )}
                        {!loading && searchResults.length === 0 && searchQuery.length >= 2 && (
                          <div className="p-4 text-white/60 text-base text-center">No products found</div>
                        )}
                        {!loading && searchQuery.length >= 2 &&
                          searchResults.map((p) => {
                            const discount = p.original_price && p.price 
                              ? Math.round(((p.original_price - p.price) / p.original_price) * 100) 
                              : 0;
                            const hasStock = p.stock > 0 || (p.variant_stock && p.variant_stock.some(v => v.stock > 0));
                            
                            return (
                              <motion.button
                                key={p.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                onClick={() => handleProductSelect(p.id)}
                                className="flex gap-4 px-5 py-4 hover:bg-[#ffcc32]/10 w-full border-b border-slate-800/30 last:border-b-0 transition"
                              >
                                <img
                                  src={getProductImage(p) || '/placeholder.svg'}
                                  alt={p.name}
                                  className="w-16 h-16 object-cover rounded flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto] gap-4 items-start max-sm:grid-cols-1 max-sm:flex-col">
                                  {/* LEFT SIDE - Product Info */}
                                  <div className="min-w-0 text-left max-sm:flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="text-white text-base font-medium line-clamp-1">{p.name}</p>
                                      <LaunchBadge product={p} className="shrink-0" />
                                    </div>
                                    
                                    {/* Price and Discount */}
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <p className="text-[#ffcc32] text-base font-semibold" style={{ fontFamily: "Arial, sans-serif" }}>Rs. {(p.price || 0).toLocaleString()}</p>
                                      {p.original_price && p.original_price > p.price && (
                                        <>
                                          <p className="text-white/50 text-sm line-through">Rs. {p.original_price.toLocaleString()}</p>
                                          <span className="bg-green-600 text-white text-sm px-2 py-0.5 rounded">{discount}% OFF</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* RIGHT SIDE - Rating, Colors, Stock */}
                                  <div className="flex flex-col items-end gap-1 min-w-[100px] flex-shrink-0 max-sm:items-start">
                                    {/* Rating */}
                                    {p.rating && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-yellow-500 text-sm">★</span>
                                        <span className="text-white text-sm">{p.rating}</span>
                                        {p.reviews_count > 0 && (
                                          <span className="text-white/60 text-sm">({p.reviews_count})</span>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Available Colors */}
                                    {p.colors && p.colors.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-white/60 text-xs">Colors:</span>
                                        <div className="flex gap-0.5">
                                          {p.colors.slice(0, 4).map((color, idx) => (
                                            <div
                                              key={idx}
                                              className="w-3 h-3 rounded-full border border-slate-600 flex-shrink-0"
                                              style={{ backgroundColor: color.toLowerCase() }}
                                              title={color}
                                            />
                                          ))}
                                          {p.colors.length > 4 && (
                                            <span className="text-white/60 text-xs">+{p.colors.length - 4}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Stock Status */}
                                    <div className="flex items-center gap-1">
                                      <span className={`text-xs font-medium ${hasStock ? 'text-green-500' : 'text-red-500'}`}>
                                        {hasStock ? '✓ In Stock' : '✗ Out of Stock'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </motion.button>
                            );
                          })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* RIGHT - CART, AUTH */}
            <div className="flex items-center gap-3 sm:gap-4 ml-auto">
              {/* Mobile Search Icon */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSearchExpanded(!searchExpanded)}
                className="lg:hidden text-white/70 hover:text-white transition p-2"
              >
                <Search size={24} />
              </motion.button>

              {/* Wishlist Icon */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (!isAuthenticated) {
                    openAuth('login')
                    return
                  }
                  navigate('/wishlist')
                }}
                className="relative hidden sm:block text-white/70 hover:text-white transition p-2"
              >
                <Heart size={24} />
                {wishlistCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-orange-500 text-black text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center shadow-lg shadow-orange-500/50"
                    style={{ fontFamily: 'Arial, sans-serif' }}
                  >
                    {wishlistCount}
                  </motion.span>
                )}
              </motion.button>

              {/* Cart */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (!isAuthenticated) {
                    openAuth('login')
                    return
                  }
                  navigate('/cart')
                }}
                className="relative text-white/70 hover:text-white transition p-2"
              >
                <ShoppingCart size={24} />
                {cartCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-orange-500 text-black text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center shadow-lg shadow-orange-500/50"
                    style={{ fontFamily: 'Arial, sans-serif' }}
                  >
                    {cartCount}
                  </motion.span>
                )}
              </motion.button>

              {/* Auth Button */}
              {isAuthenticated ? (
                <div className="relative hidden lg:block" ref={profileRef}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowProfileMenu(prev => !prev)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg hover:bg-slate-800/40 transition text-white/70 hover:text-white"
                  >
                    <User size={20} />
                    <ChevronDown
                      size={16}
                      className={`hidden sm:inline transition ${showProfileMenu ? 'rotate-180' : ''
                        }`}
                    />
                  </motion.button>

                  <AnimatePresence>
                    {showProfileMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 bg-black border border-slate-800 mt-2 w-52 rounded-lg shadow-2xl z-50 overflow-hidden backdrop-blur-xl"
                      >
                        <div className="px-5 py-4 border-b border-slate-800/50">
                          <p className="text-white text-base font-bold">{user?.name || user?.mobile}</p>
                          <p className="text-white/60 text-sm mt-1">Premium Member</p>
                        </div>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            navigate('/profile')
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-[#ffcc32]/10 hover:text-white text-base transition border-b border-slate-800/50 flex items-center gap-2"
                        >
                          <User size={16} /> My Profile
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            navigate('/orders')
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-[#ffcc32]/10 hover:text-white text-base transition border-b border-slate-800/50 flex items-center gap-2"
                        >
                          <Package size={16} /> My Orders
                        </motion.button>
                        <div className="border-t border-slate-800/50">
                          <p className="px-5 py-2 text-white/40 text-xs uppercase font-bold">Policies</p>
                        </div>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            navigate('/terms')
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-[#ffcc32]/10 hover:text-white text-base transition border-b border-slate-800/50 flex items-center gap-2"
                        >
                          Terms & Conditions
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            navigate('/privacy')
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-[#ffcc32]/10 hover:text-white text-base transition border-b border-slate-800/50 flex items-center gap-2"
                        >
                          Privacy Policy
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            navigate('/refund')
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-[#ffcc32]/10 hover:text-white text-base transition border-b border-slate-800/50 flex items-center gap-2"
                        >
                          Refund & Cancellation
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            navigate('/shipping')
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-[#ffcc32]/10 hover:text-white text-base transition border-b border-slate-800/50 flex items-center gap-2"
                        >
                          Shipping & Delivery
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 4 }}
                          onClick={() => {
                            setShowProfileMenu(false)
                            handleLogout()
                          }}
                          className="block w-full text-left px-5 py-3 text-white/70 hover:bg-red-500/10 hover:text-red-400 text-base transition flex items-center gap-2"
                        >
                          <LogOut size={16} /> Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
onClick={() => openAuth('login')}
                  className="px-5 py-3 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-lg transition text-base shadow-lg shadow-orange-500/30"
                >
                  Login
                </motion.button>
              )}
            </div>
          </div>

          {/* Mobile Search Bar */}
          <AnimatePresence>
            {searchExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden pb-4 border-t border-slate-800/50 pt-4"
                ref={mobileSearchRef}
              >
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-white/50" size={16} />
                  <input
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowResults(true)}
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700/50 text-white text-sm rounded-lg focus:outline-none focus:border-orange-500"
                  />

                  <AnimatePresence>
                    {showResults && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full mt-2 w-full bg-slate-900 border border-slate-700/50 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
                      >
                        {/* Category Quick Filters */}
                        {searchQuery.length < 2 && (
                          <div className="p-3 border-b border-slate-800/50">
                            <p className="text-slate-400 text-xs font-semibold mb-2 uppercase tracking-wide">Quick Categories</p>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { label: 'Tank Cover 411', filter: 'himalayan 411 tank cover' },
                                { label: 'Tank Cover 450', filter: 'himalayan 450 tank cover' },
                                { label: 'Lights', filter: 'lights' },
                                { label: 'Windshield', filter: 'windshield' },
                                { label: 'Gears', filter: 'gear' },
                                { label: 'Jackets', filter: 'jacket' },
                              ].map((cat, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setSearchQuery(cat.filter)}
                                  className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-xs text-white hover:bg-[#ffcc32] hover:text-black hover:border-[#ffcc32] transition"
                                >
                                  {cat.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {loading && searchQuery.length >= 2 && <div className="p-3 text-slate-400 text-sm text-center">Searching...</div>}
                        {!loading && searchResults.length === 0 && searchQuery.length >= 2 && (
                          <div className="p-3 text-slate-400 text-sm text-center">No products found</div>
                        )}
                        {!loading && searchQuery.length >= 2 &&
                          searchResults.map((p) => {
                            const discount = p.original_price && p.price 
                              ? Math.round(((p.original_price - p.price) / p.original_price) * 100) 
                              : 0;
                            const hasStock = p.stock > 0 || (p.variant_stock && p.variant_stock.some(v => v.stock > 0));
                            
                            return (
                              <motion.button
                                key={p.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                onClick={() => {
                                  handleProductSelect(p.id)
                                  setSearchExpanded(false)
                                }}
                                className="flex gap-3 px-4 py-3 hover:bg-[#ffcc32]/10 w-full border-b border-slate-800/50 last:border-b-0 transition"
                              >
                                <img
                                  src={getProductImage(p) || '/placeholder.svg'}
                                  alt={p.name}
                                  className="w-14 h-14 object-cover rounded flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto] gap-3 items-start max-sm:grid-cols-1 max-sm:flex-col">
                                  {/* LEFT SIDE - Product Info */}
                                  <div className="min-w-0 text-left max-sm:flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="text-white text-sm font-medium line-clamp-1">{p.name}</p>
                                      <LaunchBadge product={p} className="shrink-0" />
                                    </div>
                                    
                                    {/* Price and Discount */}
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <p className="text-[#ffcc32] text-sm font-semibold">Rs. {(p.price || 0).toLocaleString()}</p>
                                      {p.original_price && p.original_price > p.price && (
                                        <>
                                          <p className="text-slate-500 text-xs line-through">Rs. {p.original_price.toLocaleString()}</p>
                                          <span className="bg-green-600 text-white text-xs px-1.5 py-0.5 rounded">{discount}% OFF</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* RIGHT SIDE - Rating, Colors, Stock */}
                                  <div className="flex flex-col items-end gap-1 min-w-[80px] flex-shrink-0 max-sm:items-start">
                                    {/* Rating */}
                                    {p.rating && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-yellow-500 text-xs">★</span>
                                        <span className="text-white text-xs">{p.rating}</span>
                                        {p.reviews_count > 0 && (
                                          <span className="text-slate-400 text-xs">({p.reviews_count})</span>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Available Colors */}
                                    {p.colors && p.colors.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-slate-400 text-xs">Colors:</span>
                                        <div className="flex gap-0.5">
                                          {p.colors.slice(0, 4).map((color, idx) => (
                                            <div
                                              key={idx}
                                              className="w-2.5 h-2.5 rounded-full border border-slate-600 flex-shrink-0"
                                              style={{ backgroundColor: color.toLowerCase() }}
                                              title={color}
                                            />
                                          ))}
                                          {p.colors.length > 4 && (
                                            <span className="text-slate-400 text-xs">+{p.colors.length - 4}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Stock Status */}
                                    <div className="flex items-center gap-1">
                                      <span className={`text-xs font-medium ${hasStock ? 'text-green-500' : 'text-red-500'}`}>
                                        {hasStock ? '✓ In Stock' : '✗ Out of Stock'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </motion.button>
                            );
                          })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>

   {/* Mobile Menu Sidebar */}
<AnimatePresence>
  {isMenuOpen && (
    <>
      {/* Overlay Background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => setIsMenuOpen(false)}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
      />

      {/* Sidebar */}
      <motion.div
        initial={{ x: -400 }}
        animate={{ x: 0 }}
        exit={{ x: -400 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="fixed top-0 left-0 h-full w-[70%] max-w-xs z-50 bg-[#050f0a] border-r border-[#1a4d2e]/50 backdrop-blur-xl overflow-y-auto scrollbar-hide lg:hidden"
      >
        <div className="relative p-5 space-y-5 pb-20">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMenuOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-[#ffcc32] transition"
          >
            <X size={24} />
          </motion.button>

          {/* User info at top */}
          {isAuthenticated ? (
            <motion.button
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => { setIsMenuOpen(false); navigate('/profile') }}
              className="bg-gradient-to-br from-[#0a1a0f] to-[#050f0a] border border-[#1a4d2e]/50 rounded-xl p-4 mt-2 w-full text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center text-white font-bold shrink-0">
                  {(user?.name || user?.mobile || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-bold truncate text-sm">{user?.name || 'Rider'}</p>
                  <p className="text-emerald-400/70 text-xs truncate">{user?.mobile}</p>
                </div>
              </div>
            </motion.button>
          ) : (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => { setIsMenuOpen(false); openAuth('login') }}
              className="w-full mt-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-xl transition text-sm"
            >
              Login / Register
            </motion.button>
          )}

          {/* Navigation Links */}
          <div className="border-t border-[#1a4d2e]/40 pt-4 space-y-1">
            {[
              { label: 'Home', icon: Home, path: '/' },
              { label: 'Outran Products', icon: Zap, path: '/tank-cover' },
              { label: 'Gears', icon: Package, path: '/products?product_type=gear' },
              { label: 'Bike Parts', icon: Package, path: '/products?product_type=spare' },
              { label: 'Auxiliary Lights', icon: Zap, path: '/products?product_type=accessory&spare_category_slug=auxilary-lights' },
              { label: 'About Us', icon: null, path: '/about' },
              { label: 'About Outrans', icon: null, path: '/about-outrans' },
              { label: 'Wishlist', icon: Heart, path: '/wishlist', auth: true },
              { label: 'My Profile', icon: User, path: '/profile', auth: true },
              { label: 'My Orders', icon: Package, path: '/orders', auth: true },
            ].filter(item => !item.auth || isAuthenticated).map((item, idx) => (
              <motion.button
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ x: 4 }}
                onClick={() => { navigate(item.path); setIsMenuOpen(false) }}
                className="flex items-center gap-3 w-full py-2.5 px-3 text-white/80 hover:text-white hover:bg-emerald-500/10 transition rounded-lg font-medium text-sm"
              >
                {item.icon && <item.icon size={20} />}
                {item.label}
              </motion.button>
            ))}
          </div>

          {/* Policies Section */}
          <div className="border-t border-[#1a4d2e]/40 pt-4">
            <p className="text-white/60 text-xs uppercase font-bold mb-3">Policies</p>
            <div className="space-y-1">
              {[
                { label: 'Terms & Conditions', path: '/terms' },
                { label: 'Privacy Policy', path: '/privacy' },
                { label: 'Refund & Cancellation', path: '/refund' },
                { label: 'Shipping & Delivery', path: '/shipping' },
              ].map((item, idx) => (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ x: 4 }}
                  onClick={() => { navigate(item.path); setIsMenuOpen(false) }}
                  className="flex items-center gap-3 w-full py-2.5 px-3 text-white/80 hover:text-white hover:bg-emerald-500/10 transition rounded-lg font-medium text-sm"
                >
                  {item.label}
                </motion.button>
              ))}
            </div>
          </div>

          {isAuthenticated && (
            <div className="border-t border-[#1a4d2e]/40 pt-4 space-y-1">
              <motion.button
                whileHover={{ x: 4 }}
                onClick={handleLogout}
                className="flex items-center gap-3 w-full py-2.5 px-3 text-[#ffcc32] hover:text-orange-400 hover:bg-[#ffcc32]/10 transition rounded-lg text-sm"
              >
                <LogOut size={18} /> Logout
              </motion.button>
            </div>
          )}

          {/* Time */}
          <div className="border-t border-slate-800 pt-6 mt-6">
            <div className="flex items-center justify-center gap-2 py-4 px-4 bg-slate-900/40 rounded-lg">
              <Clock size={18} className="text-white/60" />
              <span className="text-white/80 font-medium">
                {new Date().toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>

        </div>
      </motion.div>
    </>
  )}
</AnimatePresence>

    </>
    
  )
}

export default Navbar
